import { zodResolver } from '@hookform/resolvers/zod';
import { FIELD_NAMES } from 'modules/projectSettings/constants';
import { ProjectLabel } from 'modules/projectSettings/types';
import { createPositiveToastMessage } from 'modules/projectSettings/utils';
import { addPositiveToast } from 'modules/toastContainer';
import { Controller, useController, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { Modal, MODAL_SIZES, MODAL_VARIANTS, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { z } from 'zod';
import { ProjectSettingsFormSchemaType, useProjectSettingsModalsContext } from '../../contexts';

const { LABELS } = FIELD_NAMES;

export const AddNewLabelModal = () => {
  const dispatch = useDispatch();
  const { hideModal } = useProjectSettingsModalsContext();
  /** Project Settings Page form controller */
  const {
    field: { onChange, value: labels },
  } = useController<ProjectSettingsFormSchemaType>({
    name: LABELS,
  });

  /** Local Labels form controller */
  const { control, handleSubmit } = useForm({
    defaultValues: { newLabel: '' },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: zodResolver(
      z.object({
        newLabel: z
          .string()
          .min(1, 'Name is required')
          .refine((name) => {
            return !(labels as ProjectLabel[]).find(
              (label) => label.name.toLowerCase() === name.toLowerCase().trim(),
            );
          }, 'A label with the same name already exists in your project.'),
      }),
    ),
  });

  const onSubmit = ({ newLabel }: { newLabel: string }) => {
    // assign a local id to manage the new labels
    const minId = Math.min(...(labels as ProjectLabel[]).map((label) => label.id ?? 0), 0);
    const labelToAdd = { name: newLabel.trim(), id: minId - 1 };

    onChange([...(labels as ProjectLabel[]), labelToAdd]);
    const message = createPositiveToastMessage('Label', 'added');
    dispatch(addPositiveToast(message));
    hideModal();
  };

  return (
    <Modal
      id="add-new-label-modal"
      isModalOpen={true}
      onClose={hideModal}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DEFAULT}
      withPortal
    >
      <ModalHeader>Add new label</ModalHeader>
      <ModalBody>
        <form id="add-new-label-form" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name="newLabel"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormField
                inputId="name"
                labelText="Name"
                hasError={!!error?.message}
                errorText={error?.message}
              >
                <InputText id="name" value={value} onChange={onChange} autoFocus />
              </FormField>
            )}
          />
        </form>
      </ModalBody>
      <ModalFooter onClose={hideModal}>
        <PrimaryButton form="add-new-label-form" size={BUTTON_SIZES.LARGE} type="submit">
          Add label
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
